<template>
  <!-- 货主库存出库 — 셀러가 재고에서 출고요청한 건(processStatus 출고요청) 목록. 체크 → 变更为出库待发(출고대기).
       예전 出库(warehouseManagement) 「出库请求处理」 탭 → 库存列表 탭 → 사이드바 독립 메뉴(2026-10 YZY).
       피킹용: 货主·库位码 필터, 상품 이미지, 库位号→箱号→상품 순 엑셀, 박스에 든 게 전부 출고요청이면 「整箱出库」 표시.
       데이터는 /api/reqpurchase/shipout/picking (조회 전용) -->
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="card-title mb-0">{{ pageTitle }}</h4>
          </div>
          <div class="card-body ship-req">
            <div class="d-flex align-items-center flex-wrap ship-req__bar mb-2">
              <label class="mb-0 small">货主(화주)</label>
              <select v-model="companyFilter" class="form-control form-control-sm" style="width:170px">
                <option value="">全部(전체)</option>
                <option v-for="c in companyOptions" :key="c" :value="c">{{ c }}</option>
              </select>
              <label class="mb-0 small ml-2">库位码(위치번호)</label>
              <input v-model.trim="locationFilter" type="text" class="form-control form-control-sm" style="width:130px" placeholder="A-01">
              <input v-model.trim="keyword" type="text" class="form-control form-control-sm ml-2" style="width:200px"
                     placeholder="搜索 商品名/条码/箱号(상품명·바코드·박스)">
              <b-button size="sm" variant="outline-secondary" :disabled="loading" @click="load">🔄 刷新<small>(새로고침)</small></b-button>
              <b-button size="sm" variant="success" :disabled="!filtered.length" @click="downloadExcel">⬇ Excel<small>(피킹용 엑셀 다운로드)</small></b-button>
              <span class="small text-muted">出库请求(출고요청) {{ filtered.length }} / {{ list.length }}件 · 整箱(박스 통째) {{ wholeBoxCount }}箱</span>
              <b-button size="sm" variant="success" class="ml-auto" :disabled="!selected.length || loading" @click="toWaiting">
                ✅ 已选(선택) {{ selected.length }}件 → 变更为出库待发<small>(출고대기로 변경)</small>
              </b-button>
            </div>
            <div v-if="loading" class="text-center my-4"><b-spinner variant="primary"></b-spinner></div>
            <div v-else class="table-responsive ship-req__table">
              <table class="table table-sm table-bordered table-hover mb-0 ship-req__grid">
                <thead class="thead-light">
                  <tr>
                    <th style="width:36px"><input type="checkbox" :checked="allChecked" @change="toggleAll($event.target.checked)"></th>
                    <th class="ship-req__th">图片<small>(이미지)</small></th>
                    <th v-for="c in columns" :key="c.key" class="ship-req__th" @click="sortBy(c.key)">
                      {{ c.text }} <span v-if="sort.key === c.key">{{ sort.asc ? '▲' : '▼' }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in sorted" :key="r._id" :class="{ 'ship-req__whole': r._통째 }">
                    <td><input type="checkbox" :value="r._id" v-model="selected"></td>
                    <td>
                      <a v-if="r._이미지" :href="r._이미지" target="_blank" rel="noopener">
                        <img :src="r._이미지" alt="" style="width:48px;height:48px;object-fit:cover">
                      </a>
                    </td>
                    <td><b v-if="r.제품위치">{{ r.제품위치 }}</b><span v-else class="text-muted">-</span></td>
                    <td class="ship-req__box">
                      <span v-if="r._박스">{{ r._박스 }}</span><span v-else class="text-muted">-</span>
                      <div v-if="r._통째" class="mt-1"><span class="badge badge-danger ship-req__whole-badge">📦 整箱出库(박스 통째)</span></div>
                    </td>
                    <td>{{ r.companyid }}</td>
                    <td class="ship-req__name" :title="r.상품명">{{ r.상품명 }}</td>
                    <td class="ship-req__name" :title="r._옵션">{{ r._옵션 }}</td>
                    <td>{{ r.SKUID }}</td>
                    <td>{{ r.바코드 }}</td>
                    <td class="text-right"><b>{{ r._수량 }}</b></td>
                    <td class="text-right">{{ r._박스내수량 === '' ? '-' : r._박스내수량 }}</td>
                    <td>{{ r._통관 }}</td>
                    <td><span v-if="r._출고형식" class="badge" :style="outboundTypeStyle(r._출고형식)">{{ outboundTypeText(r._출고형식) }}</span></td>
                    <td>{{ r._출고요청일 }}</td>
                  </tr>
                  <tr v-if="!sorted.length">
                    <td :colspan="columns.length + 2" class="text-center text-muted py-4">没有出库请求(출고요청 건이 없습니다)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx';
import { coupassMenuTitle } from './coupassMenu';
import { outboundTypeText, outboundTypeStyle, customsTypeText, outboundTypeOfBox } from '@/utils/coupassStatusText';

function ymd(d) {
  if (!d) return '';
  if (typeof d === 'string' && !/^\d+$/.test(d)) return d.slice(0, 10);
  const dt = new Date(typeof d === 'string' ? parseInt(d, 10) : d);
  return isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 10);
}

// 出库形式 규칙은 utils/coupassStatusText outboundTypeOfReq 한 곳(入仓登记 라벨과 공용)

// 피킹 순서: 库位号 오름차순(숫자 자연정렬, 위치 없는 건 맨 뒤) → 箱号 → 상품명
function pickCompare(a, b) {
  const la = a.제품위치 || '', lb = b.제품위치 || '';
  if (!la !== !lb) return la ? -1 : 1;
  return la.localeCompare(lb, undefined, { numeric: true })
    || (a._박스 || '~').localeCompare(b._박스 || '~', undefined, { numeric: true })
    || String(a.상품명 || '').localeCompare(String(b.상품명 || ''));
}

export default {
  name: 'ShipmentRequestList',
  data() {
    return {
      pageTitle: coupassMenuTitle('logis.logisshipoutrequest'),
      list: [],
      selected: [],
      keyword: '',
      companyFilter: '',
      locationFilter: '',
      loading: false,
      sort: { key: '_pick', asc: true },
      columns: [
        { key: '_pick', text: '库位号(위치번호)' },
        { key: '_박스', text: '箱号(박스번호)' },
        { key: 'companyid', text: '货主(화주)' },
        { key: '상품명', text: '商品名(상품명)' },
        { key: '_옵션', text: '选项(옵션)' },
        { key: 'SKUID', text: 'SKUID' },
        { key: '바코드', text: '条码(바코드)' },
        { key: '_수량', text: '出库数量(출고수량)' },
        { key: '_박스내수량', text: '箱内总数(박스 내 총수량)' },
        { key: '_통관', text: '清关方式(통관방식)' },
        { key: '_출고형식', text: '出库形式(출고형태)' },
        { key: '_출고요청일', text: '出库请求日(출고요청일)' }
      ]
    };
  },
  computed: {
    companyOptions() {
      return [...new Set(this.list.map(r => r.companyid).filter(Boolean))].sort();
    },
    filtered() {
      const k = this.keyword.toLowerCase();
      const loc = this.locationFilter.toLowerCase();
      return this.list.filter(r =>
        (!this.companyFilter || r.companyid === this.companyFilter)
        && (!loc || String(r.제품위치 || '').toLowerCase().includes(loc))
        && (!k || [r.상품명, r.바코드, r.SKUID, r._박스].some(v => String(v || '').toLowerCase().includes(k))));
    },
    sorted() {
      const { key, asc } = this.sort;
      if (key === '_pick') return this.filtered.slice().sort((a, b) => pickCompare(a, b) * (asc ? 1 : -1));
      const num = key === '_수량' || key === '_박스내수량';
      return this.filtered.slice().sort((a, b) => {
        const x = num ? (Number(a[key]) || 0) : String(a[key] || '');
        const y = num ? (Number(b[key]) || 0) : String(b[key] || '');
        return (x < y ? -1 : x > y ? 1 : 0) * (asc ? 1 : -1);
      });
    },
    allChecked() {
      return this.sorted.length > 0 && this.sorted.every(r => this.selected.includes(r._id));
    },
    wholeBoxCount() {
      return new Set(this.filtered.filter(r => r._통째).map(r => r._박스)).size;
    }
  },
  mounted() {
    // 사이드바 「货主库存出库」 알림점(셀러 출고요청) — 이 목록을 열면 본 것으로 친다
    this.$store.commit('sseClearNotification', 'shipment_requested');
    this.load();
  },
  // 상단 탭으로 다시 돌아와도 알림점은 본 것으로 친다
  activated() {
    this.$store.commit('sseClearNotification', 'shipment_requested');
  },
  methods: {
    outboundTypeText,
    outboundTypeStyle,
    sortBy(key) {
      this.sort = { key, asc: this.sort.key === key ? !this.sort.asc : true };
    },
    toggleAll(v) {
      this.selected = v ? this.sorted.map(r => r._id) : [];
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/shipout/picking', {});
        if (data.요청결과 !== '성공') {
          console.error('출고요청 목록 실패:', data.error);
          alert('搜索失败(검색 실패)');
          return;
        }
        const boxes = data.boxes || {};
        this.list = (data.rows || []).map(r => {
          const boxId = (r.출고정보 && r.출고정보.boxLabelId) || '';
          const box = boxId ? boxes[boxId] : null;
          return {
            ...r,
            바코드: typeof r.바코드 === 'string' ? r.바코드.trim() : r.바코드,
            제품위치: typeof r.제품위치 === 'string' ? r.제품위치.trim() : (r.제품위치 || ''),
            _이미지: r.옵션이미지 || r.등록이미지 || '',
            _옵션: [r.옵션1_중국어, r.옵션2_중국어].filter(Boolean).join(' '),
            _수량: parseInt(r.입고수량) || parseInt(r.구매요청수량) || 0,
            // 박스 라벨 ID 는 줄바꿈이 들어 있다 — 라벨 바코드처럼 - 로 이어 보인다
            _박스: boxId.replace(/\n/g, '-'),
            _박스내수량: box ? box.박스내수량 : '',
            _통째: !!(box && box.통째출고),
            // 通关方式 — 구매요청 요청내역.customsType(화주가 구매요청 때 고른 값). 없으면 빈칸(기본값을 짐작해 채우지 않는다)
            _통관: customsTypeText((r.요청내역 || {}).customsType),
            // 박스별 发货方式(箱子分配에서 바꾼 값)이 있으면 그것 먼저
            _출고형식: outboundTypeOfBox(box, r),
            _출고요청일: ymd(r._reqDate)
          };
        });
        this.selected = [];
      } catch (e) {
        console.error('출고요청 목록 오류:', e);
        alert('搜索中发生错误(검색 중 오류 발생)');
      } finally {
        this.loading = false;
      }
    },
    // 피킹용 엑셀 — 지금 걸러진 목록을 库位号 → 箱号 → 상품 순으로(화면 정렬과 무관)
    downloadExcel() {
      const rows = this.filtered.slice().sort(pickCompare);
      const header = ['库位号(위치번호)', '箱号(박스번호)', '货主(화주)', '商品名(상품명)', '选项(옵션)', '条码(바코드)',
        '出库数量(출고수량)', '箱内总数(박스 내 총수량)', '整箱出库(박스 통째)', '清关方式(통관방식)', '出库形式(출고형태)', '图片URL(이미지URL)'];
      const IMG = header.length - 1;
      const aoa = [header].concat(rows.map(r => [
        r.제품위치 || '', r._박스, r.companyid || '', r.상품명 || '', r._옵션, r.바코드 || '',
        r._수량, r._박스내수량, r._통째 ? '整箱出库(박스 통째)' : '', r._통관, outboundTypeText(r._출고형식), r._이미지
      ]));
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      rows.forEach((r, i) => {
        const c = ws[XLSX.utils.encode_cell({ r: i + 1, c: IMG })];
        if (r._이미지 && c) c.l = { Target: r._이미지, Tooltip: r._이미지 };
      });
      ws['!cols'] = [12, 22, 14, 40, 16, 16, 10, 10, 14, 18, 18, 40].map(w => ({ wch: w }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      const d = new Date();
      const stamp = d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
      XLSX.writeFile(wb, '库存出库_' + (this.companyFilter || '全部') + '_' + stamp + '.xlsx');
    },
    async toWaiting() {
      if (!this.selected.length) return;
      if (!confirm(`将已选的 ${this.selected.length} 件变更为出库待发吗？\n(선택한 ${this.selected.length}건을 출고대기로 변경하시겠습니까?)\n(出库请求 → 出库待发)`)) return;
      this.loading = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/updateProcessStatusBulk', {
          ids: this.selected, toStatus: '출고대기', memo: '쿠패스 출고요청 처리 (货主库存出库)'
        });
        if (data.요청결과 === '성공') {
          this.$bvToast.toast(`${data.successCount}件已变更为出库待发(출고대기로 변경되었습니다).`, { title: '处理完成(처리 완료)', variant: 'success', solid: true });
        } else {
          alert('处理失败(처리 실패): ' + JSON.stringify(data));
        }
      } catch (e) {
        console.error('출고요청 처리 오류:', e);
        alert('处理中发生错误(처리 중 오류 발생)');
      } finally {
        this.loading = false;
        this.load();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.ship-req {
  &__bar { gap: 8px; }
  &__table {
    max-height: 640px;
    overflow-y: auto;
    thead th { position: sticky; top: 0; z-index: 1; }
  }
  &__th { cursor: pointer; white-space: nowrap; user-select: none; }
  &__name { max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__box { white-space: nowrap; font-size: 12px; }
  &__whole td { background: #fff4f4; }
  &__whole-badge { font-size: 12px; padding: 4px 6px; }
  &__grid { font-size: 13px; td { vertical-align: middle; } }
}
</style>
